/**
 * @file: The Preflight report on the calendar page.
 *
 * This class opens and closes the report, and opens the interval of a report
 * row in the panel. The panel belongs to the state manager. This class uses
 * only two members of it: mayReplacePanel and showInPanel.
 */

/**
 * Give the stored interval of a report row in the shape of a calendar event.
 * The panel opens on a bar through showInPanel. The row then uses the same
 * path, and the panel reads the same fields.
 */
function asCalendarEvent (interval) {
  const { start, end, intervalId, sceneCollectionId, role, updatedAt } = interval

  return { start, end, extendedProps: { intervalId, sceneCollectionId, role, updatedAt } }
}

export class PreflightManager {
  constructor (controller, SceneCollectionService) {
    this.controller = controller
    this.SceneCollectionService = SceneCollectionService

    // The preflight report. Null means closed. An open report is { loading },
    // { scenes } or { error }, so the card always has content to show.
    this.preflight = null
  }

  /**
     * Ask the server what never reaches the screen, and open the report on it.
     *
     * The check covers this scene only. The calendar is a page for one scene,
     * and the operator cannot repair a scene that is not on show. The check
     * covers the full period, and not the week on show. An interval can lose to
     * an interval that the current view does not include.
     *
     * A failed call puts the reason in the card, and does not close the card.
     * The operator asked a question, and an empty screen would look like the
     * answer.
     */
  openPreflight () {
    this.preflight = { loading: true }

    return this.SceneCollectionService.preflight(this.controller.scene && this.controller.scene.id)
      .then(scenes => {
        // Do not show a late answer to a question that the operator closed.
        if (!this.preflight) {
          return
        }

        this.preflight = { scenes: scenes || [] }
      })
      .catch(error => {
        this.controller._$log.error('[ScheduleCalendar] Preflight failed:', error)

        if (!this.preflight) {
          return
        }

        this.preflight = {
          error: (error && error.message) || 'Could not check the plan. Check the connection and try again.'
        }
      })
  }

  closePreflight () {
    this.preflight = null

    // The report pointed at a bar while the operator read a row. Nothing points
    // at a bar now, whichever way the card closed.
    if (this.controller.calendarManager) {
      this.controller.calendarManager.highlightInterval(null)
    }
  }

  /**
   * Close the report, open the interval of one row in the panel, and show
   * the interval in the grid.
   *
   * The row has the stored values of the interval, so the panel does not need
   * the bar. The interval can be outside the week on show. The grid then goes
   * to its week, and the fetch of that week draws the bar with the selection
   * ring. The panel can open before the grid holds the interval. The panel then
   * shows no standing until the fetch completes.
   *
   * The question about unsaved edits comes before the grid moves. The report
   * is a modal card, so it closes first.
   *
   * @param {Object} interval - { intervalId, sceneCollectionId, role, start,
   *   end, updatedAt } of the row
   */
  openPreflightRow (interval) {
    const calendarManager = this.controller.calendarManager

    // Ask before the grid moves. A refusal keeps the card and the panel.
    if (!this.controller.stateManager.mayReplacePanel()) {
      return
    }

    this.closePreflight()

    if (!calendarManager) {
      return
    }

    this.controller.stateManager.showInPanel(asCalendarEvent(interval))
    calendarManager.showTime(interval.start)
  }
}
